import { Portal } from '@mantine/core';
import { getFullName } from '@zorro/common-utils';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Title } from '@zorro/design-system/v2/Title';
import { singleLineTruncatedTextWithEllipsis } from '@zorro/shared/formatters';
import { MODAL_ZINDEX } from '@zorro/types';
import { Box, Grid, Group, Stack } from '@zorro/web/design-system';
import { useAppRouter, useMonolithQuery } from '@zorro/web/ui-utils';
import { ReactNode } from 'react';

import { useAnalytics } from '../../Analytics';
import { CommentsPopover } from '../../Comments/CommentsPopover';
import { EnrollmentAllowanceCard } from '../../EnrollmentAllowanceCard';
import { FormFooter } from '../../FormFooter';
import { ManualSubmissionStepper } from './ManualSubmissionStepper';
import {
  ManualSubmissionStepChangeDirection,
  ManualSubmissionStepSubmit,
  ManualSubmissionStepType,
  getManualSubmissionRoute,
  getManualSubmissionStepConfig,
} from './ManualSubmissionUtils';

type Props = {
  step: ManualSubmissionStepType;
  children: ReactNode;
  onSubmit: ManualSubmissionStepSubmit;
  submitLabel?: string;
  isSubmitDisabled?: boolean;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  isDon?: boolean;
  withReviewCard?: boolean;
};

export const ManualSubmissionStep = ({
  step,
  children,
  onSubmit,
  isSubmitDisabled,
  submitLabel = 'Continue',
  employerId,
  employeeId,
  onboardingPeriodId,
  isDon = false,
  withReviewCard = false,
}: Props) => {
  const { navigateToRoute } = useAppRouter();
  const { sendEvent } = useAnalytics();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const title = employee
    ? `Manual election for ${getFullName(employee)}`
    : 'Manual election';

  const { nextRoute, prevStep, nextStep, withSkipButton } =
    getManualSubmissionStepConfig(step, isDon, onboardingPeriod);

  const sendStepChangeEvent = async (
    toStep: ManualSubmissionStepType,
    direction: ManualSubmissionStepChangeDirection
  ) => {
    await sendEvent('manual_election_step_change', {
      onboarding_period_id: onboardingPeriodId,
      from_step: step,
      to_step: toStep,
      direction,
      trigger: 'footer',
    });
  };

  const handleBack = async () => {
    if (prevStep) {
      await sendStepChangeEvent(
        prevStep,
        ManualSubmissionStepChangeDirection.BACK
      );
      navigateToRoute(
        getManualSubmissionRoute(isDon),
        [
          {
            employerId,
            employeeId,
            onboardingPeriodId,
          },
        ],
        {
          initialOPId: onboardingPeriodId,
          step: prevStep,
        }
      );
    }
  };

  const handleSkip = async () => {
    await sendStepChangeEvent(
      nextStep,
      ManualSubmissionStepChangeDirection.SKIP
    );
    navigateToRoute(
      nextRoute,
      [
        {
          employerId,
          employeeId,
          onboardingPeriodId,
        },
      ],
      {
        initialOPId: onboardingPeriodId,
        step: nextStep,
      }
    );
  };

  const handleSubmit = async () => {
    const { navigate } = await onSubmit();
    if (!navigate) {
      if (step !== ManualSubmissionStepType.REVIEW) {
        await sendEvent('manual_election_step_submit_blocked', {
          onboarding_period_id: onboardingPeriodId,
          step,
        });
      }
      return;
    }

    if (step !== ManualSubmissionStepType.REVIEW) {
      await sendStepChangeEvent(
        nextStep,
        ManualSubmissionStepChangeDirection.FORWARD
      );
    }

    navigateToRoute(
      nextRoute,
      [
        {
          employerId,
          employeeId,
          onboardingPeriodId,
        },
      ],
      {
        initialOPId: onboardingPeriodId,
        step: nextStep,
      }
    );
  };

  return (
    <>
      <Stack mih="calc(100vh - 18.5rem)" pl="xl" pb="5rem" gap={0}>
        <Stack
          style={{ zIndex: MODAL_ZINDEX + 1 }}
          justify="space-between"
          bg="zorroWhite.0"
          pos="sticky"
          mx="-1.5rem"
          px="1.5rem"
          h="9rem"
          pt="md"
          top="0"
        >
          <Group justify="space-between" wrap="nowrap">
            <Box maw="50rem" style={singleLineTruncatedTextWithEllipsis}>
              <Title order={1} display="inline">
                {title}
              </Title>
            </Box>
            <CommentsPopover
              onboardingPeriodId={onboardingPeriodId}
              employerId={employerId}
              employeeId={employeeId}
            />
          </Group>
          <Divider />
          <ManualSubmissionStepper
            onboardingPeriodId={onboardingPeriodId}
            employerId={employerId}
            employeeId={employeeId}
            isDon={isDon}
            step={step}
          />
          <Divider />
        </Stack>
        <Grid overflow="visible" pt="xl">
          <Grid.Col span={{ base: 8, xl: 9 }}>
            <Stack w="100%" gap="xl">
              {children}
            </Stack>
          </Grid.Col>
          {withReviewCard && (
            <Grid.Col
              span={{ base: 4, xl: 3 }}
              h="min-content"
              pos="sticky"
              top="10rem"
            >
              <Group justify="end">
                <EnrollmentAllowanceCard
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                />
              </Group>
            </Grid.Col>
          )}
        </Grid>
      </Stack>
      <Portal target="#manual-submission-footer">
        <FormFooter
          primaryLabel={submitLabel}
          primaryButtonProps={{
            onClick: handleSubmit,
            disabled: isSubmitDisabled,
          }}
          secondaryLabel={withSkipButton ? 'Skip' : undefined}
          secondaryButtonProps={{ onClick: handleSkip }}
          tertiaryLabel={prevStep ? 'Back' : undefined}
          tertiaryButtonProps={{ onClick: handleBack, mr: 'auto' }}
          paperProps={{ pos: 'sticky', bottom: '0' }}
        />
      </Portal>
    </>
  );
};
